import React, { useEffect, useMemo } from 'react'
import { defer } from 'lodash'
import { motion, AnimatePresence } from 'motion/react'
import { Button } from '@mc/design-system'
import { useBucket } from '@mc/experiments-react'
import { useTranslation } from '@mc/i18n'
import { track, TRACK_PROPS } from '@mc/track-event'
import { useTrackingContext } from '@mc/tracking-context'
import { useVideo } from '../VideoContext'
import classes from './VideoSkips.module.scss'

export type { Skip } from '@mc/edge-api'

// Feature flag for skip intro button
// https://app.growthbook.io/features/ce-3287-skip-button-web
const SKIP_INTRO_FLAG = 'ce-3287-skip-button-web'

const SKIPS_MAP = {
  intro: {
    text: 'video.skip.intro',
    tracking: {
      modal_type: 'skip_intro',
      action: 'Skip Intro',
    },
  },
}

interface VideoSkipsProps {
  className?: string
}

const timeToSeconds = (time: number) => Math.floor(time / 1000)

const timeInRange = (time: number, start: number, end: number) =>
  time >= timeToSeconds(start) + 1 && time < timeToSeconds(end) - 5
const timeInAutomaticShowRange = (time: number, start: number) =>
  time >= timeToSeconds(start) + 1 && time < timeToSeconds(start) + 6

export const VideoSkips: React.FC<VideoSkipsProps> = ({ className = '' }) => {
  const { t } = useTranslation('@mc/video')

  // FEATURE FLAG
  const skipIntroEnabled = useBucket(SKIP_INTRO_FLAG) === 'on'

  // TRACKING PARAMS
  const {
    [TRACK_PROPS.CONTENT_TYPE]: videoContentType,
    [TRACK_PROPS.CONTENT_ID]: contentId,
    [TRACK_PROPS.PAGE_TYPE]: pageType,
  } = useTrackingContext()

  const trackingParams = {
    [TRACK_PROPS.CONTENT_TYPE]: videoContentType,
    [TRACK_PROPS.CONTENT_ID]: contentId,
    [TRACK_PROPS.PAGE_TYPE]: pageType,
  }

  // STATE
  const { time, skips, seekTo, seekingTo, interacting } = useVideo()

  const activeSkip = useMemo(
    () =>
      skips?.find(
        (skip) =>
          time >= timeToSeconds(skip.start) && time < timeToSeconds(skip.end),
      ),
    [time, skips],
  )

  const mappedSkip = useMemo(
    () => SKIPS_MAP[activeSkip?.type as keyof typeof SKIPS_MAP],
    [activeSkip?.type],
  )

  const showButton =
    skipIntroEnabled &&
    activeSkip &&
    mappedSkip &&
    ((interacting && timeInRange(time, activeSkip.start, activeSkip.end)) ||
      (!interacting && timeInAutomaticShowRange(time, activeSkip.start)))

  // TRACKING
  useEffect(() => {
    if (showButton && activeSkip) {
      track('Modal Viewed', {
        ...trackingParams,
        modal_type: mappedSkip.tracking.modal_type,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showButton, activeSkip?.type])

  const trackSkipped = () => {
    if (!activeSkip) return

    track('Modal Clicked', {
      ...trackingParams,
      ...mappedSkip.tracking,
      action: t(mappedSkip.text),
    })
  }

  // ACTIONS
  const handleSkipClick = (event: React.MouseEvent) => {
    // Prevent the click from bubbling up to the video player's play/pause handler
    event.stopPropagation()
    event.preventDefault()

    if (!activeSkip) return

    // Track skip button click
    trackSkipped()

    // Seek to end of skip segment - proper seek flow with deferred seekTo
    seekingTo(timeToSeconds(activeSkip.end))
    defer(seekTo, timeToSeconds(activeSkip.end))
  }

  if (!skipIntroEnabled) {
    return null
  }

  return (
    <div className={`${classes.skipContainer} ${className}`}>
      <AnimatePresence>
        {activeSkip && showButton && (
          <motion.div
            key={activeSkip.type}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <Button
              kind='play'
              size='sm'
              onClick={handleSkipClick}
              className='mc-clickable'
            >
              {t(mappedSkip.text)}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
